import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ObligationDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type {
  AssessmentResultParentWithObligationResultsResponseRow,
  ObligationRegisterResponseRow,
} from '@risksmart-app/drizzle/src/types';
import type { ObligationRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetObligationsQuery,
  Obligation_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetObligationsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { OBLIGATION_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  tagsWhereOnEntity,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';

const mapTrpcAssessmentResultParentToGraphQL = (
  assessment_result_parent: AssessmentResultParentWithObligationResultsResponseRow
): StrictMappedGraphQLSelection<
  GetObligationsQuery['assessment_result_parent'][number]
> => ({
  ...assessment_result_parent,
});

const mapTrpcObligationToGraphQL = (
  obligation: ObligationRegisterResponseRow
): StrictMappedGraphQLSelection<GetObligationsQuery['obligation'][number]> => ({
  ...obligation,
  Parent: obligation.parent,
  BreachedIssues: obligation.issues,
  CreatedByUser: obligation.CreatedByUser,
  ModifiedByUser: obligation.ModifiedByUser,
  CreatedBy: obligation.createdBy,
  ModifiedBy: obligation.modifiedBy,
  controls_aggregate: {
    aggregate: {
      count: obligation.controls.length,
    },
  },
});

export const mapTrpcObligationsToGraphQL = (
  trpcData: ObligationRegisterResponse | undefined
): StrictMappedGraphQLSelection<GetObligationsQuery> | undefined => {
  if (!trpcData) {
    return undefined;
  }

  return {
    obligation: trpcData.obligation.map(mapTrpcObligationToGraphQL),
    assessment_result_parent: trpcData.assessment_result_parent.map(
      mapTrpcAssessmentResultParentToGraphQL
    ),
  };
};

const useObligationsGraphqlVariables = (
  args: UseGetObligationsRegisterArgs
) => {
  const { user } = useRisksmartUser();

  // Explicit type arguments: obligation has no `base` (its own predicate is
  // `undefined`), so there is nothing to anchor `TWhere` for inference — see
  // the oversight hook's identical comment.
  const { where } = buildDashboardWhere<
    DashboardFilterArgs<ObligationDashboardDateField>,
    Obligation_Bool_Exp
  >({
    filters: args,
    userId: user?.userId,
    datePredicates: OBLIGATION_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.obligationFilterConditions,
  });

  return {
    includeAssessmentResultsHistory: true,
    ...(where ? { where } : {}),
  };
};

export type UseGetObligationsRegisterArgs =
  DashboardFilterArgs<ObligationDashboardDateField>;

export const useGetObligationsRegister = createQueryHook<
  UseGetObligationsRegisterArgs,
  ObligationRegisterResponse,
  GetObligationsQuery
>({
  trpcQueryOptions: (
    trpc,
    { tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.obligation.register.queryOptions(
      toTrpcDashboardFilterInput({
        tagTypeIds,
        departmentTypeIds,
        dateRange,
        ownership,
      })
    ),
  mapTrpcDataToGraphQL: (data) => ({
    obligation: data.obligation.map(mapTrpcObligationToGraphQL),
    assessment_result_parent: data.assessment_result_parent.map(
      mapTrpcAssessmentResultParentToGraphQL
    ),
  }),
  graphqlDocument: GetObligationsDocument,
  graphqlVariables: useObligationsGraphqlVariables,
});
